import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Check, WifiOff, History } from 'lucide-react';
import { useNetworkStatus } from '@shared/hooks/useNetworkStatus';
import { useOfflineSync } from '@shared/hooks/useOfflineSync';
import { useAuth } from '@shared/contexts/AuthContext';
import { offlineDataManager } from '@shared/lib/offlineData';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { cn } from '@shared/lib/utils';

// Ensures the one-time first-run seed only fires once per session, even though
// this component may mount on multiple pages.
let seedTriggered = false;

const formatLastSync = (time: Date | null) => {
  if (!time) return 'Never';
  const diff = Date.now() - time.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export default function SyncButton() {
  const navigate = useNavigate();
  const { isOnline } = useNetworkStatus();
  const { isSyncing, pendingOperations, lastSyncTime, syncNow } = useOfflineSync();
  const { isAuthenticated } = useAuth();

  // First-run seeding: pull once if local storage has never been populated.
  useEffect(() => {
    if (isAuthenticated && isOnline && !isSyncing && !seedTriggered && !offlineDataManager.isSeeded()) {
      seedTriggered = true;
      void syncNow();
    }
  }, [isAuthenticated, isOnline, isSyncing, syncNow]);

  if (!isAuthenticated) return null;

  // Keep the navbar clean when everything is already in sync — only surface the
  // control when there are changes to push, a sync in progress, or we're offline.
  if (isOnline && !isSyncing && pendingOperations === 0) return null;

  // Status → color. Red = offline or changes waiting to sync.
  const needsAttention = !isOnline || pendingOperations > 0;
  const statusColor = needsAttention ? 'text-red-500' : 'text-green-500';
  const dotColor = needsAttention ? 'bg-red-500' : 'bg-green-500';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" title="Sync">
          {isSyncing ? (
            <RefreshCw className="h-[1.2rem] w-[1.2rem] animate-spin text-muted-foreground" />
          ) : !isOnline ? (
            <WifiOff className={cn('h-[1.2rem] w-[1.2rem]', statusColor)} />
          ) : pendingOperations > 0 ? (
            <RefreshCw className={cn('h-[1.2rem] w-[1.2rem]', statusColor)} />
          ) : (
            <Check className={cn('h-[1.2rem] w-[1.2rem]', statusColor)} />
          )}
          {pendingOperations > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
              {pendingOperations}
            </span>
          )}
          <span className="sr-only">Sync</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-2 py-1.5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className={cn('h-2 w-2 rounded-full', dotColor)} />
            {isOnline ? 'Online' : 'Offline'}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {pendingOperations > 0
              ? `${pendingOperations} change(s) waiting to sync`
              : `Last synced: ${formatLastSync(lastSyncTime)}`}
          </p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={syncNow} disabled={isSyncing || !isOnline}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isSyncing && 'animate-spin')} />
          <span>{isSyncing ? 'Syncing…' : 'Sync now'}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/sync')}>
          <History className="mr-2 h-4 w-4" />
          <span>Sync history</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
